/**
 * Walk-in visit history — working out which drop-ins belong to the same person.
 *
 * A walk-in carries no member id (see WalkIn in types.ts), so "has this person
 * been in before" has to be reconstructed from what was typed on the form:
 * phone when there is one, the name otherwise. Shared between the front desk
 * (Dexie) and the portal (Supabase) so a "3rd visit" badge means the same
 * thing in both — see frontdesk/screens/WalkInHistory.tsx and
 * portal/Dashboard.tsx.
 */
import { normalizePhone } from './identity';

/** The fields grouping needs — enough to work from either a Dexie or a Supabase row. */
export interface WalkInLike {
  phone: string;
  person_name: string;
  visited_at: string;
  created_at: string;
}

export interface WalkInVisit {
  /** This visit's place in the person's history, oldest = 1. */
  visitNumber: number;
  /** How many times this person has walked in, in total — not just in the current view. */
  totalVisits: number;
}

/**
 * The key two walk-ins are considered "the same person" by.
 *
 * Phone is required on the form (see validateWalkIn), so the name fallback
 * only fires for data seeded before that rule existed. Name matching is
 * case-insensitive because "Ahmed" and "ahmed" typed on two different visits
 * is one repeat customer, not two different people.
 */
export function walkInIdentityKey(w: Pick<WalkInLike, 'phone' | 'person_name'>): string {
  const phone = normalizePhone(w.phone);
  if (phone) return `p:${phone}`;
  return `n:${w.person_name.trim().toLowerCase()}`;
}

/**
 * Annotate each walk-in with its visit number and the person's total visit
 * count, so a repeat drop-in — someone worth offering a membership to — is
 * visible without cross-referencing anything else.
 *
 * Input order is preserved; this only adds fields. Ties within one person's
 * history break the same way the recent-walk-ins list does — visited_at, then
 * created_at — because several drop-ins can share a date.
 */
export function annotateWalkInVisits<T extends WalkInLike>(
  walkIns: readonly T[]
): (T & WalkInVisit)[] {
  const groups = new Map<string, T[]>();
  for (const w of walkIns) {
    const key = walkInIdentityKey(w);
    const list = groups.get(key);
    if (list) list.push(w);
    else groups.set(key, [w]);
  }

  // Keyed by object identity rather than id: callers may pass plain objects
  // (e.g. Supabase rows) that have no guaranteed id field at this layer.
  const visitNumberOf = new Map<T, number>();
  for (const group of groups.values()) {
    [...group]
      .sort(
        (a, b) =>
          a.visited_at.localeCompare(b.visited_at) || a.created_at.localeCompare(b.created_at)
      )
      .forEach((w, i) => visitNumberOf.set(w, i + 1));
  }

  return walkIns.map((w) => ({
    ...w,
    visitNumber: visitNumberOf.get(w)!,
    totalVisits: groups.get(walkInIdentityKey(w))!.length,
  }));
}

/** "2nd", "3rd", "4th" — English ordinal suffix, for the visit badge. */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
